import { ParameterId as Api, VerbId as ApiVerbId } from '@tableau/api-internal-contract-js';
import { DocCommands as PresLayerDocCommandId, UICommands as PresLayerUICommandId } from '../../gen-src/preslayer/CommandIds';
import { ParameterId as PL } from '../../gen-src/preslayer/Params';
import { ApiContext } from '../ApiContext';
import { CommandMappingRegistry } from './CommandMappingRegistry';
import { CommandMappingRegistryFactory } from './CommandMappingRegistryFactory';
import { CommandNamespace, CommandParameter, CommandRegistration } from './CommandRegistration';

export class ExtensionsCommandMappingRegistryFactory {
  /**
   * Creates and populates a new CommandMappingRegistry for Extensions
   *
   * @static
   * @returns {CommandMappingRegistry}
   * @memberof ExtensionsCommandMappingRegistryFactory
   */
  public static createCommandMappingRegistry(): CommandMappingRegistry {
    let result: CommandMappingRegistry = CommandMappingRegistryFactory.createCommandMappingRegistry();
    const apiContext = ApiContext.getInstance();

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.InitializeExtension,
        PresLayerDocCommandId.InitializeAddInInstance,
        [
          new CommandParameter(Api.ExtensionLocator, PL.AddInLocator),
          new CommandParameter(Api.ExtensionContextMenuIds, PL.ExtensionContextMenuIds),
          new CommandParameter(Api.IsExtensionDialog, PL.IsExtensionDialog),
          CommandParameter.makeOptional(Api.ExtensionDialogPayload, PL.ExtensionDialogPayload),
        ],
        [new CommandParameter(Api.ExtensionBootstrapInfo, PL.AddInBootstrapInfo)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.BlockExtension, PresLayerDocCommandId.BlockExtension, [
        new CommandParameter(Api.ExtensionLocator, PL.AddInLocator),
      ]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetHighlightedMarks,
        PresLayerDocCommandId.GetHighlightedData,
        [new CommandParameter(Api.VisualId, PL.VisualIDPM)],
        [new CommandParameter(Api.HighlightedData, PL.HighlightedData)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetAllDataSources,
        PresLayerDocCommandId.ApiGetDataSchemaPresModel,
        [],
        [new CommandParameter(Api.DataSchema, PL.DataSchema)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SaveExtensionSettings,
        PresLayerDocCommandId.SaveAddInSettings,
        [new CommandParameter(Api.ExtensionLocator, PL.AddInLocator), new CommandParameter(Api.SettingsValues, PL.AddInSettings)],
        [new CommandParameter(Api.ExtensionSettingsInfo, PL.AddInSettingsInfo)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.DisplayDialog,
        PresLayerUICommandId.DisplayExtensionDialog,
        [
          new CommandParameter(Api.ExtensionLocator, PL.AddInLocator),
          new CommandParameter(Api.ExtensionDialogUrl, PL.ExtensionDialogUrl),
          new CommandParameter(Api.ExtensionDialogH, PL.ExtensionDialogH),
          new CommandParameter(Api.ExtensionDialogW, PL.ExtensionDialogW),
          CommandParameter.makeOptional(Api.ExtensionDialogPayload, PL.ExtensionDialogPayload),
          CommandParameter.makeOptional(Api.ExtensionDialogStyle, PL.ExtensionDialogStyle),
        ],
        [new CommandParameter(Api.ExtensionDialogResult, PL.ExtensionDialogResult)],
        CommandNamespace.TabUI,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.CloseDialog,
        PresLayerUICommandId.CloseExtensionDialog,
        [
          new CommandParameter(Api.ExtensionLocator, PL.AddInLocator),
          CommandParameter.makeOptional(Api.ExtensionDialogPayload, PL.ExtensionDialogPayload),
        ],
        undefined,
        CommandNamespace.TabUI,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SetClickThrough,
        apiContext.isDesktop() ? PresLayerDocCommandId.SetClickThrough : PresLayerDocCommandId.WebSetPointerEvents,
        [
          apiContext.isDesktop()
            ? new CommandParameter(Api.ExtensionLocator, PL.AddInLocator)
            : new CommandParameter(Api.ExtensionZoneId, PL.ZoneID), // On server, we need the zoneId, which acts like the AddInLocator
          new CommandParameter(
            Api.ClickThroughEnabled,
            apiContext.isDesktop() ? PL.IsTransparentForMouseEvents : PL.PointerEventsShouldBeSetToAuto,
          ),
        ],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.SetZoneVisibility, PresLayerDocCommandId.SetZoneHiddenOrVisible, [
        new CommandParameter(Api.Dashboard, PL.Dashboard),
        new CommandParameter(Api.ZoneIdsVisibilityMap, PL.ZoneIdsVisibilityMap),
      ]),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.MoveAndResizeZones, PresLayerDocCommandId.MoveAndResizeZones, [
        new CommandParameter(Api.Dashboard, PL.Dashboard),
        new CommandParameter(Api.DashboardObjectPositionAndSizeUpdateList, PL.ZonePositionAndSizeUpdateList),
      ]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ReplayAnimation,
        apiContext.isDesktop() ? PresLayerUICommandId.ReplayAnimation : PresLayerDocCommandId.WebReplayAnimation,
        [new CommandParameter(Api.ReplaySpeed, PL.ReplaySpeed)],
        undefined,
        apiContext.isDesktop() ? CommandNamespace.TabUI : CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.VizAPI,
        PresLayerDocCommandId.VizAPI,
        [new CommandParameter(Api.VizAPIInputJson, PL.VizAPIInputJson)],
        [new CommandParameter(Api.VizAPISVG, PL.VizAPISVG)],
      ),
    );

    return result;
  }
}
